import { Flex } from '@mantine/core';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { CampaignClass } from '@feathr/blackbox';
import { CampaignLabelMap, ECampaignClass } from '@feathr/blackbox';
import type { TTheme } from '@feathr/components';
import { Icon, Pill, PixelWellWithHighlight, Tooltip, Well } from '@feathr/components';
import { CampaignIcon } from '@feathr/extender/components/CampaignIcons';
import type { TLicenseFeatures } from '@feathr/extender/hooks';
import {
  useGoogleAdsPermissions,
  useLicense,
  useMinPackageForFeature,
} from '@feathr/extender/hooks';
import { useAccount } from '@feathr/extender/state';

import {
  assertCampaignClassKey,
  hasEmailSendingErrors,
  hasMetaIntegrationErrors,
} from './CampaignClassCard.utils';
import DisabledFBTooltip from './DisabledFBTooltip';
import useCampaignDescriptionMap from './useCampaignDescriptionMap';

import * as styles from './CampaignClassCard.css';

export interface ICardProps {
  cls: CampaignClass;
  onClick: (type: CampaignClass) => void;
  usageText?: string | null;
  isNew?: boolean;
  layout?: 'vertical' | 'horizontal';
  recommended?: boolean;
}

function CampaignClassCard({
  cls,
  onClick,
  usageText,
  isNew,
  layout = 'vertical',
  recommended = false,
}: Readonly<ICardProps>): JSX.Element {
  const { t, campaignDescriptionMap } = useCampaignDescriptionMap();
  const account = useAccount();
  const metaIntegrationState = account.get('facebook').state;
  const hasGoogleAdsPermission = useGoogleAdsPermissions();

  // ECampaignClass has human readable keys, and references to the CampaignClass as the values
  const campaignClassKey = Object.entries(ECampaignClass).find(
    ([, value]) => (value as unknown as CampaignClass) === cls,
  )?.[0] as keyof TLicenseFeatures | undefined;

  assertCampaignClassKey(campaignClassKey);

  const hasPackageAccess = useLicense(campaignClassKey);
  const hasEmailsSuspended = hasEmailSendingErrors({ cls, account });
  const hasMetaCampaignsDisabled = hasMetaIntegrationErrors({ cls, account });
  const hasGoogleCampaignsDisabled =
    !hasGoogleAdsPermission && campaignClassKey === 'googleAdsSmart';

  const isDisabled =
    !hasPackageAccess ||
    hasEmailsSuspended ||
    hasMetaCampaignsDisabled ||
    hasGoogleCampaignsDisabled;
  const minPackage = useMinPackageForFeature(account.activePackage, cls);
  const metaTooltip = hasMetaCampaignsDisabled && (
    <DisabledFBTooltip cls={cls} state={metaIntegrationState} />
  );
  const locked = !hasPackageAccess;

  const name = CampaignLabelMap.get(cls);
  const title = (
    <h4 className={styles.title}>
      {name}
      {locked && <Icon.Locked />}
    </h4>
  );

  function handleClick(): void {
    if (!isDisabled) {
      onClick(cls);
    }
  }

  const WrapperEl = recommended ? PixelWellWithHighlight : Well;

  const sharedProps = {
    className: classNames({}, styles.root, {
      [styles.disabled]: isDisabled,
      [styles.horizontal]: layout === 'horizontal',
      [styles.vertical]: layout === 'vertical',
      [styles.recommended]: recommended,
    }),
    padding: 'relaxed',
    onClick: handleClick,
    'data-appcues': cls,
    disabled: isDisabled,
  };
  const props = recommended
    ? {
        highlighted: true,
        name: CampaignLabelMap.get(cls),
        contentClassName: styles.content,
        ...sharedProps,
      }
    : {
        theme: 'white' as TTheme,
        ...sharedProps,
      };

  const getTooltip = () => {
    if (!hasPackageAccess) {
      return t('Upgrade to {{package}} to access this feature', {
        package: minPackage,
      });
    }

    if (hasMetaCampaignsDisabled) {
      return metaTooltip;
    }

    if (hasGoogleCampaignsDisabled) {
      return t(
        'Log in to your Google Ads account via Feathr to launch Google Ad Grants campaigns.',
      );
    }

    if (hasEmailsSuspended) {
      return t('Email campaigns are currently suspended');
    }

    return undefined;
  };

  return (
    <Tooltip
      className={isDisabled ? 'disabled-tooltip-trigger' : undefined}
      floating={true}
      title={getTooltip()}
    >
      <WrapperEl {...props}>
        <CampaignIcon
          className={styles.icon}
          cls={cls}
          disabled={isDisabled}
          hover={false}
          size={48}
        />
        <div>
          <Flex>
            {title}
            {isNew && <Pill theme={'cyan'}>New</Pill>}
          </Flex>
          <p className={styles.description}>{campaignDescriptionMap.get(cls)}</p>
          {usageText && <p>{usageText}</p>}
          {hasEmailsSuspended && (
            <p className={styles.error}>
              {t('Your Feathr account is suspended and cannot publish any new email campaigns.')}
            </p>
          )}
        </div>
      </WrapperEl>
    </Tooltip>
  );
}

export default observer(CampaignClassCard);
